// Enemy content, verbatim from v1 js/constants.js:377-490 (ENEMY_TYPES,
// ENEMY_SPEED_CONFIG). v1 variant district keys (prenzlauer2-4 etc.) are
// collapsed to the 7 real district ids. `abilities` stay flavor strings
// until Phase 5 makes them behavioral.

export interface EnemyDef {
  id: string;
  name: string;
  speed: number;
  aggressiveness: number;
  jumpHeight: number;
  districts: readonly string[];
  spawnWeight: number;
  colors: {
    hair: readonly number[];
    clothing: readonly number[];
    accessories: readonly number[];
  };
  abilities: readonly string[];
  scoreValue: number;
}

export const ENEMY_TYPES: Record<string, EnemyDef> = {
  punk: {
    id: 'punk',
    name: 'Punk',
    speed: 1.6,
    aggressiveness: 0.9,
    jumpHeight: 0.8,
    districts: ['kreuzberg'],
    spawnWeight: 0.3,
    colors: {
      hair: [0x000000, 0xff0000, 0x800080, 0x00ff00],
      clothing: [0x000000, 0x330000, 0x660000, 0x003300],
      accessories: [0xffffff, 0xff0000, 0xffff00],
    },
    abilities: ['fast_movement', 'aggressive_chase'],
    scoreValue: 60,
  },
  hipster: {
    id: 'hipster',
    name: 'Hipster',
    speed: 1.3,
    aggressiveness: 0.4,
    jumpHeight: 0.6,
    districts: ['prenzlauer'],
    spawnWeight: 0.25,
    colors: {
      hair: [0x8b4513, 0x654321, 0x4a4a4a],
      clothing: [0x8b4513, 0x654321, 0x2f4f4f, 0x708090],
      accessories: [0x8b4513, 0x654321, 0x000000],
    },
    abilities: ['coffee_boost', 'distracted_walking'],
    scoreValue: 40,
  },
  business: {
    id: 'business',
    name: 'Business Person',
    speed: 1.4,
    aggressiveness: 0.7,
    jumpHeight: 0.5,
    districts: ['mitte'],
    spawnWeight: 0.35,
    colors: {
      hair: [0x4a4a4a, 0x8b4513, 0x000000],
      clothing: [0x000000, 0x2f2f2f, 0x4a4a4a],
      accessories: [0x000000, 0x8b4513, 0x654321],
    },
    abilities: ['phone_distraction', 'briefcase_swing'],
    scoreValue: 50,
  },
  jogger: {
    id: 'jogger',
    name: 'Suburban Jogger',
    speed: 1.8,
    aggressiveness: 0.3,
    jumpHeight: 1.0,
    districts: ['zehlendorf', 'frohnau', 'pankow'],
    spawnWeight: 0.2,
    colors: {
      hair: [0x8b4513, 0x654321, 0x4a4a4a, 0xffffff],
      clothing: [0x00ff00, 0x0000ff, 0xff0000, 0xff8c00, 0x800080],
      accessories: [0xffffff, 0x000000, 0x8b4513],
    },
    abilities: ['athletic_jump', 'endurance_run'],
    scoreValue: 70,
  },
  artist: {
    id: 'artist',
    name: 'Street Artist',
    speed: 1.3,
    aggressiveness: 0.6,
    jumpHeight: 0.7,
    districts: ['kreuzberg'],
    spawnWeight: 0.25,
    colors: {
      hair: [0xff0000, 0x00ff00, 0x0000ff, 0xff8c00, 0x800080],
      clothing: [0x4b0082, 0x8b008b, 0x2f4f4f, 0x708090],
      accessories: [0xffff00, 0xff0000, 0x00ff00, 0x0000ff],
    },
    abilities: ['paint_splash', 'parkour_move'],
    scoreValue: 55,
  },
  pedestrian: {
    id: 'pedestrian',
    name: 'Elegant Pedestrian',
    speed: 1.2,
    aggressiveness: 0.5,
    jumpHeight: 0.4,
    districts: ['schoeneberg'],
    spawnWeight: 0.3,
    colors: {
      hair: [0x8b4513, 0x654321, 0x4a4a4a, 0xffffff],
      clothing: [0x8b4513, 0x654321, 0x2f2f2f, 0x4a4a4a],
      accessories: [0x8b4513, 0x654321, 0x000000, 0xffffff],
    },
    abilities: ['graceful_dodge', 'umbrella_swing'],
    scoreValue: 45,
  },
};

/** Sensei (Phase 5): belt-gated boss encounter. Not part of the normal
 *  district spawn pool — spawned explicitly on belt promotion. */
export const SENSEI_DEF: EnemyDef = {
  id: 'sensei',
  name: 'Sensei',
  speed: 1.5,
  aggressiveness: 1.0,
  jumpHeight: 1.0,
  districts: [],
  spawnWeight: 0,
  colors: {
    hair: [0xdddddd],
    clothing: [0x1a1a1a],
    accessories: [0xffffff],
  },
  abilities: ['belt_trial'],
  scoreValue: 100,
};

// v1 js/constants.js:471-490
export const ENEMY_SPEED = {
  MIN_SPEED_MULTIPLIER: 1.2,
  PLAYER_BASE_SPEED: 1.0,
  MIN_SPEED_ADVANTAGE: 0.2,
  MAX_SPEED_MULTIPLIER: 3.0,
  DIFFICULTY_SCALING: 0.1,
} as const;

/** Dynamic enemy speed — verbatim v1 obstacle-manager.js:3913-3944. */
export function dynamicEnemySpeed(def: EnemyDef, gameSpeed: number): number {
  const playerEffective = gameSpeed * ENEMY_SPEED.PLAYER_BASE_SPEED;
  const minRequired = playerEffective + gameSpeed * ENEMY_SPEED.MIN_SPEED_ADVANTAGE;

  let speed = gameSpeed * def.speed;
  if (speed < minRequired) {
    speed = Math.max(minRequired, gameSpeed * ENEMY_SPEED.MIN_SPEED_MULTIPLIER);
  }
  const difficultyLevel = Math.floor(gameSpeed * 10);
  speed += gameSpeed * (difficultyLevel * ENEMY_SPEED.DIFFICULTY_SCALING);
  speed = Math.min(speed, gameSpeed * ENEMY_SPEED.MAX_SPEED_MULTIPLIER);
  return Math.max(speed, gameSpeed * 1.15);
}

/** Weighted enemy pick for a district (v1 selectEnemyType). */
export function selectEnemyType(districtId: string, roll: number): EnemyDef {
  const candidates = Object.values(ENEMY_TYPES).filter((e) => e.districts.includes(districtId));
  const pool = candidates.length > 0 ? candidates : Object.values(ENEMY_TYPES);
  const totalWeight = pool.reduce((sum, e) => sum + e.spawnWeight, 0);
  let r = roll * totalWeight;
  for (const def of pool) {
    r -= def.spawnWeight;
    if (r <= 0) return def;
  }
  return pool[pool.length - 1] as EnemyDef;
}
